import { Check, Eye, Link2, Pencil, Plus, RefreshCw, Sparkles, Upload, type LucideIcon } from "lucide-react";
import { useState } from "react";

import type { JDDetail, JDEvent } from "@/api/jd-queries";
import { Button } from "@/components/ui/button";
import { Empty, Panel, PanelHeader } from "@/components/ui/panel";
import { Segmented } from "@/components/ui/segmented";
import { cn, formatDate } from "@/lib/utils";

/**
 * Activity — the JD's `jd_events` (DESIGN §20.4, §20.8), read as sentences with who and when. The raw
 * record is a click away, because the sentence is a summary and the audit trail is the record; the kind
 * stays on the right in mono so a DESIGN term still greps to the row that wrote it.
 */

type Group = "all" | "status" | "details" | "questions" | "matching";

const KIND_GROUP: Record<string, Group> = {
  status: "status",
  edited: "details",
  skill: "details",
  skill_added: "details",
  uploaded: "details",
  analysed: "details",
  reanalysed: "details",
  question: "questions",
  question_removed: "questions",
  questions_added: "questions",
  questions_uploaded: "questions",
  generate: "questions",
  promoted: "questions",
  rematched: "matching",
};

const STATUS_WORD: Record<string, string> = {
  active: "Published",
  archived: "Archived",
  discarded: "Discarded",
  in_review: "Back in review",
};

type Detail = Record<string, unknown>;
const str = (v: unknown) => (typeof v === "string" || typeof v === "number" ? String(v) : "");
const keysOf = (v: unknown) => (v && typeof v === "object" ? Object.keys(v) : []);

/** The sentence and the icon for one event, from its kind and the detail orchestration wrote. */
function describe(e: JDEvent): { icon: LucideIcon; title: string; line: string } {
  const d: Detail = e.detail ?? {};
  switch (e.kind) {
    case "status":
      return {
        icon: Check,
        title: STATUS_WORD[str(d.to)] ?? `Moved to ${str(d.to)}`,
        line: `${str(d.from)} → ${str(d.to)}${d.reason ? ` · “${str(d.reason)}”` : ""}`,
      };
    case "uploaded":
      return { icon: Upload, title: "Uploaded", line: str(d.filename) || "pasted text" };
    case "analysed":
      return {
        icon: Eye,
        title: "Read by the AI",
        line: `${str(d.skills)} skills, ${str(d.questions)} questions written in the JD`,
      };
    case "reanalysed":
      return { icon: Eye, title: "Sent to the AI again", line: d.job_id ? `job #${str(d.job_id)}` : "" };
    case "edited":
      return { icon: Pencil, title: "Details edited", line: keysOf(d.after).join(", ").replaceAll("_", " ") };
    case "skill":
      return {
        icon: Pencil,
        title: `${str(d.skill)} changed`,
        line: keysOf(d.after)
          .filter((k) => {
            const before = (d.before ?? {}) as Detail;
            const after = (d.after ?? {}) as Detail;
            return JSON.stringify(before[k]) !== JSON.stringify(after[k]);
          })
          .join(", ")
          .replaceAll("_", " "),
      };
    case "skill_added":
      return { icon: Plus, title: `${str(d.skill)} added as a skill`, line: str(d.status) };
    case "questions_added":
      return {
        icon: Plus,
        title: `${str(d.added)} question${d.added === 1 ? "" : "s"} added`,
        line: `${d.linked ? `${str(d.linked)} already in the bank and linked · ` : ""}suggested for ${d.scope === "bank" ? "the whole bank" : "this JD only"}`,
      };
    case "questions_uploaded":
      return { icon: Upload, title: "A file of questions uploaded", line: str(d.filename) };
    case "generate":
      return {
        icon: Sparkles,
        title: `AI asked to draft ${str(d.count)} questions for ${str(d.skill)}`,
        line: `under ${str(d.stack)} · job #${str(d.job_id)}`,
      };
    case "question":
      return {
        icon: Link2,
        title: `Question #${str(d.question)} changed`,
        line: keysOf(d.after).join(", ").replaceAll("_", " "),
      };
    case "question_removed":
      return { icon: Link2, title: `Question #${str(d.question)} removed from this JD`, line: str(d.origin) };
    case "promoted":
      return {
        icon: Check,
        title: "Questions promoted to the whole bank",
        line: Array.isArray(d.questions) ? d.questions.map((q) => `#${str(q)}`).join(", ") : "",
      };
    case "rematched":
      return {
        icon: RefreshCw,
        title: "Matched to the bank again",
        line: `${str(d.matched)} matched · ${str(d.added)} new · ${str(d.removed)} no longer close enough`,
      };
    default:
      return { icon: Pencil, title: e.kind.replaceAll("_", " "), line: "" };
  }
}

function EventRow({ e }: { e: JDEvent }) {
  const [raw, setRaw] = useState(false);
  const { icon: Icon, title, line } = describe(e);
  return (
    <li className="border-bank-border grid grid-cols-[130px_28px_minmax(0,1fr)_auto] items-start gap-3 border-t px-4 py-3 first:border-t-0">
      <span className="text-bank-muted pt-1 font-mono text-[11px]">{formatDate(e.at)}</span>
      <span
        className={cn(
          "grid size-7 place-items-center rounded-full",
          e.kind === "status" ? "bg-bank-good-wash text-bank-good" : "bg-bank-surface-2 text-bank-muted",
        )}
      >
        <Icon className="size-3.5" aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <p className="text-[13.5px]">
          <strong>{title}</strong>
          {e.actor && <span className="text-bank-muted"> by {e.actor}</span>}
        </p>
        {line && <p className="text-bank-muted text-[12.5px]">{line}</p>}
        {e.detail && (
          <>
            <Button size="sm" variant="ghost" className="-ml-2.5 h-6" onClick={() => setRaw(!raw)}>
              {raw ? "Hide the raw record" : "Show the raw record"}
            </Button>
            {raw && (
              <pre className="bg-bank-surface-2 mt-1 overflow-x-auto rounded-md p-2 font-mono text-[11px] whitespace-pre-wrap">
                {JSON.stringify(e.detail, null, 2)}
              </pre>
            )}
          </>
        )}
      </div>
      <span className="text-bank-muted font-mono text-[11px]">{e.kind}</span>
    </li>
  );
}

export function ActivityStep({ jd }: { jd: JDDetail }) {
  const [group, setGroup] = useState<Group>("all");
  const count = (g: Group) => jd.events.filter((e) => (KIND_GROUP[e.kind] ?? "details") === g).length;
  const shown = jd.events.filter((e) => group === "all" || (KIND_GROUP[e.kind] ?? "details") === group);
  return (
    <Panel className="overflow-hidden">
      <PanelHeader
        title="Activity"
        actions={
          <Segmented<Group>
            label="Filter activity"
            value={group}
            onChange={setGroup}
            options={[
              { value: "all", label: "All", count: jd.events.length },
              { value: "status", label: "Status", count: count("status") },
              { value: "details", label: "Details & skills", count: count("details") },
              { value: "questions", label: "Questions", count: count("questions") },
              { value: "matching", label: "Matching", count: count("matching") },
            ]}
          />
        }
      />
      {shown.length === 0 ? (
        <Empty>Nothing here yet.</Empty>
      ) : (
        <ul>
          {shown.map((e) => (
            <EventRow key={e.id} e={e} />
          ))}
        </ul>
      )}
    </Panel>
  );
}
